import { format, isAfter, subHours } from "date-fns";
import { sv } from "date-fns/locale";
import { useMemo, useState } from "react";
import { useParams } from "react-router";

import { useGlobalContext } from "app/store/GlobalContext";
import { isInternalUser } from "common/MetaActions";
import { splitName } from "common/splitName/splitName";
import { Icon } from "components/icon/Icon";
import Persona from "components/persona/Persona";
import { filterAndSortMessages } from "helpers/inbox/messageAndThreadHelpers";
import { MessageItem, THREAD_CATEGORY_LABELS, ThreadCategory, Thread as ThreadModel } from "models/inbox/inbox";
import { useRootStore } from "store";
import ThreadSidebarMessageItem from "../threadSiedebarMessageItem";

import { Attach16Regular } from "@fluentui/react-icons";
import "./ThreadSidebarItem.scss";

type Props = {
  thread: ThreadModel;
  threadLink: string;
  isSubscribing: boolean;
};

export default function ThreadSidebarItem({ thread, threadLink }: Props) {
  const { handleNavigation } = useGlobalContext();
  const [toggleMessages, setToggleMessages] = useState(false);

  const { threadId, unread, title, lastMessage, category = ThreadCategory.None } = thread;

  const { threadId: currentThread } = useParams();

  const {
    fetchThread, setSelectedMessageId
  } = useRootStore((state) => state.inbox);

  const { workingCustomer: customer } = useRootStore(
    (state) => state.customers
  );

  const {
    customerNumber: customerId,
  } = customer;


  const { messages: visibleMessages } = useMemo(() => {
    return filterAndSortMessages(thread?.messages);
  }, [thread.messages]);

  const hasAttachments = useMemo(() => {
    return visibleMessages.some(message => message.attachments && message.attachments.length > 0);
  }, [visibleMessages]);

  const { formatedFrom, createdAt, body } = lastMessage;

  const sender = splitName(formatedFrom);

  const yesterday = subHours(new Date(), 24);
  const messageDate = new Date(createdAt);
  const isRecent = isAfter(messageDate, yesterday);

  const displayDate = isRecent
    ? format(messageDate, "HH:mm")
    : format(messageDate, "yyyy/MM/dd", { locale: sv });

  const cleanedBody = useMemo(() => {
    // Create a temporary element to extract text content only
    const temp = document.createElement('div');
    temp.innerHTML = body.replace(/<br\s*\/?>/gi, ' '); // Replace <br> with space
    const textOnly = temp.textContent || temp.innerText || '';

    // Clean up whitespace
    return textOnly
      .replace(/\s+/g, ' ')  // Replace multiple whitespace with single space
      .trim();                // Remove leading/trailing whitespace
  }, [body]);

  const createMarkup = () => {
    return { __html: cleanedBody };
  };

  const handleSelectThread = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    const target = e.target as HTMLElement;
    if (target.closest('.thread-sidebar-message-wrapper')) {
      return;
    }

    handleNavigation({
      url: threadLink,
      eventTitle: "Thread",
      e: e,
      clickEvent() {
        if (window.location.pathname === threadLink) {
          fetchThread(customerId, threadId);
        }
      },
    });
    setSelectedMessageId("");
  };

  const handleArrowClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setToggleMessages((prev) => !prev);
  };

  const ThreadSidebarMessageList = () => (
    <>
      {visibleMessages.map((message: MessageItem) => {
        return (
          <ThreadSidebarMessageItem
            message={message}
            key={message.messageId}
            threadId={threadId}
            threadLink={threadLink}
          />
        );
      })}
    </>
  );

  return (
    <div
      className={`d-flex flex-direction-column thread-sidebar-wrapper ${currentThread === threadId ? "sidebar-item-active" : ""}`}
    >
      <div
        className={`sidebar-item d-flex ${unread ? "sidebar-item-unread" : ""}`}
        onClick={(e) => handleSelectThread(e)}
      >
        <div className="thread-meta mt-3 mr-2 ml-2">

          <span className="thread-meta-arrow">
            {visibleMessages.length > 1 ? (
              <Icon
                name={`fas ${toggleMessages ? "fa-chevron-down" : "fa-chevron-right"} cursor-pointer`}
                onClick={handleArrowClick}
              />
            ) : null}
          </span>
        </div>

        <div className="d-flex align-items-start w-100">
          <Persona
            firstName={sender.firstName}
            lastName={sender.lastName}
            className="mr-3 mt-2 message-person"
          />

          <div className="d-flex flex-direction-column w-100">
            <div className="d-flex justify-content-between mt-1">
              <div className="d-flex align-items-center gap-2">
                <span className="mr-3 message-sender">{formatedFrom}</span>
                {category !== ThreadCategory.None && isInternalUser() && (
                  <span
                    className={`category-badge category-badge-${ThreadCategory[category].toLowerCase()}`}>
                    {THREAD_CATEGORY_LABELS[category]}
                  </span>
                )}
              </div>

              <div className="d-flex align-items-center gap-2">
                {hasAttachments &&
                  <Attach16Regular />
                }
                <div className="message-time fw-normal">{displayDate}</div>
              </div>

            </div>

            <div className="d-flex justify-content-between my-1 html-editor-truncate-text">
              <span className="sidebar-item-title">{title}</span>
            </div>

            <div
              className="sidebar-item-message html-editor html-editor-truncate-text"
              dangerouslySetInnerHTML={createMarkup()}
            />

            {toggleMessages && ThreadSidebarMessageList()}
          </div>
        </div>
      </div>
    </div>
  );
}
